import React, { useCallback, useEffect, useState } from 'react';
import { elegantMenuProps } from '../../utils/menuStyles';
import {
  Box, Stack, Typography, Button, TextField, InputAdornment,
  Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, IconButton, Chip, Alert, Menu, MenuItem,
  Skeleton, alpha, useTheme, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import {
  Search, MoreVert, Add, Discount as DiscountIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeConcessionService, { FeeConcession } from '../../services/feeConcessionService';
import { useDebounce } from '../../hooks/useDebounce';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import ConcessionFormDialog from '../../components/fees/ConcessionFormDialog';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { ChipTone, ToneChip } from '../../components/common/StatusChip';

const DEFAULT_ROWS_PER_PAGE = 25;

/** Where a concession stands: in force, waiting for a second person, sent back, or revoked. */
const statusOf = (r: FeeConcession): { label: string; tone: ChipTone } =>
  r.isActive ? { label: 'Active', tone: 'success' }
    : r.approvalStatus === 'PENDING' ? { label: 'Waiting for approval', tone: 'pending' }
    : r.approvalStatus === 'REJECTED' ? { label: 'Sent back', tone: 'neutral' }
    : { label: 'Revoked', tone: 'neutral' };

const FeeConcessionsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();

  // Reads share the `fees` scope (list surfaces on other fee screens too);
  // writes are gated on the dedicated concession scope so approving
  // discounts stays with authorised staff.
  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fee_concessions');

  const [rows, setRows] = useState<FeeConcession[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<FeeConcession | null>(null);
  // Kept apart from the menu's target: closing the menu must not forget what is being revoked.
  const [revoking, setRevoking] = useState<FeeConcession | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feeConcessionService.listConcessions({
        includeInactive,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(res.concessions);
      setTotal(res.pagination.totalItems);
      setHasLoaded(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load concessions', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [includeInactive, search, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const currencyFmt = useCurrencyFormat();

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: FeeConcession) => {
    setMenuAnchor(e.currentTarget);
    setMenuTarget(row);
  };
  const closeMenu = () => { setMenuAnchor(null); setMenuTarget(null); };

  const openRevoke = () => {
    setRevokeReason('');
    setRevoking(menuTarget);
    closeMenu();
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    try {
      await feeConcessionService.revokeConcession(revoking._id, revokeReason.trim() || undefined);
      enqueueSnackbar('Concession revoked', { variant: 'success' });
      setRevoking(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to revoke', { variant: 'error' });
    }
  };

  if (!canReadFees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" icon={<BlockIcon />}>You don't have permission to view concessions.</Alert>
      </Box>
    );
  }

  const skeletonRows = Array.from({ length: 6 }).map((_, i) => (
    <TableRow key={i}>
      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
      <TableCell><Skeleton variant="text" width="60%" /></TableCell>
      <TableCell><Skeleton variant="text" width={80} /></TableCell>
      <TableCell><Skeleton variant="text" width={100} /></TableCell>
      <TableCell><Skeleton variant="text" width={80} /></TableCell>
      <TableCell><Skeleton variant="text" width={80} /></TableCell>
      <TableCell align="right"><Skeleton variant="circular" width={24} height={24} /></TableCell>
    </TableRow>
  ));

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Fee Concessions</Typography>
          <Typography variant="body2" color="text.secondary">
            Recurring reductions applied to a student's future challans. Historical bills are not affected — use a Waiver on an existing challan for that.
          </Typography>
        </Box>
        {canWriteFees && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
          >
            New concession
          </Button>
        )}
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name, GR number or label…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeInactive}
                onChange={(_, v) => { setIncludeInactive(v); setPage(0); }}
              />
            }
            label={<Typography variant="body2">Show revoked and sent back</Typography>}
          />
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
              <TableCell>Student</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Applies to</TableCell>
              <TableCell>Valid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ pr: 2 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !hasLoaded ? (
              skeletonRows
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <DiscountIcon sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                    No concessions yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {canWriteFees ? 'Add one for a student to reduce their future challans.' : 'Ask an admin to add a concession.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(r => (
                <TableRow
                  key={r._id}
                  hover
                  sx={{
                    opacity: r.isActive || r.approvalStatus === 'PENDING' ? 1 : 0.55,
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.studentName ?? '—'}</Typography>
                    {r.grNumber && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block' }}>
                        {r.grNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.label}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={r.mode === 'percent' ? `${r.amount}%` : currencyFmt(r.amount)}
                      sx={{
                        height: 22, fontWeight: 700, fontSize: '0.72rem',
                        borderColor: alpha(theme.palette.primary.main, 0.35),
                        color: theme.palette.primary.dark,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {r.appliesTo === 'all' ? (
                      <Typography variant="body2" color="text.secondary">All categories</Typography>
                    ) : (
                      <Tooltip title={(r.categoryNames ?? []).join(', ')}>
                        <Typography variant="body2" color="text.secondary">
                          {(r.categoryNames ?? []).length} categor{(r.categoryNames ?? []).length === 1 ? 'y' : 'ies'}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(r.validFrom), 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.validTo ? `→ ${format(new Date(r.validTo), 'MMM d, yyyy')}` : 'Evergreen'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ToneChip size="small" {...statusOf(r)} />
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 1 }}>
                    {canWriteFees && r.isActive && (
                      <IconButton size="small" onClick={e => openMenu(e, r)} aria-label="Row actions">
                        <MoreVert fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu} {...elegantMenuProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={openRevoke} sx={{ color: 'error.main' }}>Revoke</MenuItem>
      </Menu>

      <ConcessionFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => { load(); }}
      />

      <ConfirmationDialog
        open={!!revoking}
        title="Revoke concession?"
        message={revoking ? `Revoke the concession "${revoking.label}"? Future challans for this student won't apply this reduction any more. Historical challans are unchanged.` : ''}
        confirmText="Revoke"
        confirmColor="error"
        onConfirm={confirmRevoke}
        onClose={() => setRevoking(null)}
      >
        <TextField
          fullWidth
          size="small"
          label="Reason (optional)"
          value={revokeReason}
          onChange={e => setRevokeReason(e.target.value)}
          multiline
          minRows={2}
          sx={{ mt: 1 }}
        />
      </ConfirmationDialog>
    </Box>
  );
};

export default FeeConcessionsPage;
